import classNames from 'classnames';
import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import {
	Icon,
	IconArrowBigRight,
	IconArrowDown,
	IconArrowUp,
	IconNewTab,
} from '@flixbus/honeycomb-icons-react';
import { Link } from '@flixbus/honeycomb-react';

import TimezoneTabs from 'common/TimezoneTabs';

import { ASSIGNMENT_DETAILS_SIDEBAR_TEXT, getRideLabel, getRideToggleLabel } from '../text';
import RouteBlock from './RouteBlock';

import styles from './styles.module.css';

const adaptStation = (station) => ({
	...station,
	plannedDeparture: station?.departure ?? null,
});

const getSequenceStations = (rideStations, fromStation, toStation) => {
	const stations = Array.isArray(rideStations) ? rideStations : [];
	const fromStationId = fromStation?.id;
	const toStationId = toStation?.id;

	if (!fromStationId || !toStationId) return stations;

	const fromIndex = stations.findIndex((station) => station?.id === fromStationId);
	const toIndex = stations.findIndex((station) => station?.id === toStationId);

	if (fromIndex < 0 || toIndex < 0 || toIndex < fromIndex) return stations;

	return stations.slice(fromIndex, toIndex + 1);
};

const getStationName = (station) => station?.name ?? station?.code ?? 'N/A';

const RouteLabel = ({ fromStationName, toStationName }) => (
	<>
		<span className={styles.routeStation}>{fromStationName}</span>
		<Icon InlineIcon={IconArrowBigRight} size={3} aria-hidden />
		<span className={styles.routeStation}>{toStationName}</span>
	</>
);

const RideDetailsLink = ({ to, isStandalone, children }) => (
	<Link
		RouterLink={RouterLink}
		to={to}
		extraClasses={classNames(styles.link, {
			[styles.standaloneRideLink]: isStandalone,
			[styles.rideHeaderRow]: !isStandalone,
		})}
		target="_blank"
		rel="noopener noreferrer"
	>
		<span
			className={classNames(styles.linkLabel, {
				[styles.rideHeaderText]: !isStandalone,
			})}
		>
			{children}
		</span>
		<span className={styles.noUnderline}>
			<Icon
				InlineIcon={IconNewTab}
				size={3}
				aria-label={ASSIGNMENT_DETAILS_SIDEBAR_TEXT.opensInNewTab}
			/>
		</span>
	</Link>
);

const RideBlock = ({
	rideNumber,
	ride,
	line,
	fromStation,
	toStation,
	extraActivities,
	workAssignments,
	isStandalone = false,
	isCurrent = false,
}) => {
	const { pathname } = useLocation();
	const [collapsed, setCollapsed] = useState(false);
	const stations = getSequenceStations(ride?.stations, fromStation, toStation).map(adaptStation);
	const timezone = stations[0]?.timezone;
	const rideDetailsPath = ride?.id ? `/details/${ride.id}` : null;
	const isCurrentRideDetailsPage = pathname.replace(/\/$/, '') === rideDetailsPath;
	const rideTitle = getRideLabel(rideNumber, isCurrent);
	const fromStationName = getStationName(fromStation);
	const toStationName = getStationName(toStation);
	const rideRouteLabel = (
		<RouteLabel fromStationName={fromStationName} toStationName={toStationName} />
	);
	const rideLinkLabel = isStandalone ? ASSIGNMENT_DETAILS_SIDEBAR_TEXT.rideDetails : rideRouteLabel;
	const canLinkToRideDetails = rideDetailsPath && !isCurrentRideDetailsPage;
	const rideDetailsLink = canLinkToRideDetails ? (
		<RideDetailsLink to={rideDetailsPath} isStandalone={isStandalone}>
			{rideLinkLabel}
		</RideDetailsLink>
	) : null;
	const plainRideRoute = (
		<div className={styles.rideHeaderRow}>
			<span className={classNames(styles.rideHeaderText, styles.routeText)}>{rideRouteLabel}</span>
			<span className={styles.rideHeaderAction} aria-hidden />
		</div>
	);
	const rideRoute = rideDetailsLink ?? (isStandalone ? null : plainRideRoute);

	const rideBody = (
		<div
			className={classNames(styles.rideBody, {
				[styles.standaloneRideBody]: isStandalone,
			})}
		>
			<div className={styles.rideMeta}>
				<div>
					<span className={styles.bold}>{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.line}</span>{' '}
					{line?.code ?? ride?.lineCode ?? 'N/A'}
				</div>
				<div>
					<span className={styles.bold}>{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.tripNumber}</span>{' '}
					{ride?.tripNumber ?? 'N/A'}
				</div>
				<div className={styles.uuid}>
					<span className={styles.bold}>{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.uuid}</span>{' '}
					{ride?.id ?? 'N/A'}
				</div>
			</div>

			{isStandalone && rideRoute}
			{isStandalone && (
				<div className={classNames(styles.bold, styles.routeLabel)}>
					{ASSIGNMENT_DETAILS_SIDEBAR_TEXT.route}
				</div>
			)}

			<TimezoneTabs stationTimezone={timezone} instanceId={`ride-${ride?.id ?? rideNumber}`}>
				<RouteBlock
					stations={stations}
					extraActivities={extraActivities}
					workAssignments={workAssignments}
				/>
			</TimezoneTabs>
		</div>
	);

	if (isStandalone) {
		return (
			<div data-testid={`ride-block-${rideNumber}`} className={styles.standaloneRideBlock}>
				{rideBody}
			</div>
		);
	}

	return (
		<div data-testid={`ride-block-${rideNumber}`} className={styles.rideBlock}>
			<div className={styles.rideHeader}>
				<div className={styles.rideHeaderRow}>
					<span className={classNames(styles.bold, styles.rideHeaderText)}>{rideTitle}</span>
					<button
						type="button"
						className={classNames(styles.rideHeaderAction, styles.rideToggle)}
						onClick={() => setCollapsed((prev) => !prev)}
						aria-expanded={!collapsed}
						aria-label={getRideToggleLabel(collapsed, rideNumber)}
					>
						<Icon InlineIcon={collapsed ? IconArrowDown : IconArrowUp} />
					</button>
				</div>
				{rideRoute}
			</div>

			{!collapsed && rideBody}
		</div>
	);
};

export default RideBlock;
